var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getUsers } from '@web/lib/demo/utils';
import { CommunicationNetworkGraphChannelEnum, diffComparisonValueFormat } from '@workalizer/shared/types';
import { getPercentageDiff, getPercentagePointDiff } from '@workalizer/shared/utils';
var EXTERNAL_NODE_ID = 0;
var CROSS_TEAM_NODE_ID = -1;
var DEMO_EXTERNAL_LINK_TOP_PARTNERS = [
    {
        id: 'vendor',
        name: 'vendor@acme.io',
        sharePct: 62,
        previousSharePct: 40,
        kindKey: 'external'
    },
    {
        id: 'partner',
        name: 'partner@client.com',
        sharePct: 38,
        previousSharePct: 25,
        kindKey: 'external'
    }
];
var buildDemoExternalLinkTopPartners = function () {
    return DEMO_EXTERNAL_LINK_TOP_PARTNERS.map(function (partner) { return ({
        id: partner.id,
        name: partner.name,
        sharePct: partner.sharePct,
        diff: getPercentageDiff(partner.previousSharePct, partner.sharePct),
        kindKey: partner.kindKey
    }); });
};
var randInt = function (min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
var randDiff = function () {
    var previous = randInt(10, 5000);
    var current = randInt(10, 5000);
    return getPercentageDiff(previous, current);
};
var randDurationDiff = function () {
    var previous = randInt(1800, 72000);
    var current = randInt(1800, 72000);
    return getPercentageDiff(previous, current, {
        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
    });
};
var randStat = function (min, max) { return ({
    value: String(randInt(min, max)),
    diff: randDiff()
}); };
var meetDurationValue = function () {
    if (Math.random() < 0.35) {
        return "".concat(randInt(1, 12), "h ").concat(randInt(0, 59), "m");
    }
    return "".concat(randInt(15, 180), "m");
};
var randFormatBreakdown = function (channel, metricKey) {
    var buildSide = function () {
        if (channel === CommunicationNetworkGraphChannelEnum.Meet &&
            metricKey === 'received') {
            return {
                oneToOne: { value: meetDurationValue(), diff: randDurationDiff() },
                group: { value: meetDurationValue(), diff: randDurationDiff() }
            };
        }
        return {
            oneToOne: randStat(0, 24),
            group: randStat(0, 24)
        };
    };
    return {
        source: buildSide(),
        target: buildSide()
    };
};
var buildMetricStats = function (channel, isExternalLink) {
    if (channel === CommunicationNetworkGraphChannelEnum.Meet) {
        if (isExternalLink) {
            return {
                sourceSent: { value: '3', diff: getPercentageDiff(3, 3) },
                targetSent: { value: '0', diff: getPercentageDiff(0, 0) },
                sourceReceived: {
                    value: '3h 15m',
                    diff: getPercentageDiff(11700, 11700)
                },
                targetReceived: { value: '0', diff: getPercentageDiff(0, 0) }
            };
        }
        return {
            sourceSent: randStat(4, 42),
            targetSent: randStat(4, 42),
            sourceReceived: { value: meetDurationValue(), diff: randDurationDiff() },
            targetReceived: { value: meetDurationValue(), diff: randDurationDiff() },
            sourceAvgMeetingLength: {
                value: meetDurationValue(),
                diff: randDurationDiff()
            },
            targetAvgMeetingLength: {
                value: meetDurationValue(),
                diff: randDurationDiff()
            }
        };
    }
    if (isExternalLink) {
        return {
            sourceSent: { value: '1', diff: getPercentageDiff(0, 1) },
            targetSent: { value: '0', diff: getPercentageDiff(0, 0) },
            sourceReceived: { value: '2', diff: getPercentageDiff(0, 2) },
            targetReceived: { value: '0', diff: getPercentageDiff(0, 0) }
        };
    }
    return {
        sourceSent: { value: '12', diff: getPercentageDiff(7, 12) },
        targetSent: { value: '4', diff: getPercentageDiff(2, 4) },
        sourceReceived: { value: '4', diff: getPercentageDiff(2, 4) },
        targetReceived: { value: '12', diff: getPercentageDiff(7, 12) }
    };
};
var isExternalLinkInput = function (input) {
    return input.sourceNodeId === EXTERNAL_NODE_ID ||
        input.targetNodeId === EXTERNAL_NODE_ID;
};
var isEmployeeScopePersonToPersonEmailLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isEmployeeScopePersonToPersonChatLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0 &&
        (input.sourceNodeId === input.userId || input.targetNodeId === input.userId);
};
var isEmployeeScopePersonToPersonMeetLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0 &&
        (input.sourceNodeId === input.userId || input.targetNodeId === input.userId);
};
var demoUserPrimaryOrgUnitId = function (userId) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = getUsers({ userId: userId })[0]) === null || _a === void 0 ? void 0 : _a.userOrgUnits) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.orgUnitId) !== null && _d !== void 0 ? _d : null; };
var isEmployeeScopeCrossTeamPersonToPersonMeetLinkInput = function (input) {
    if (!isEmployeeScopePersonToPersonMeetLinkInput(input) ||
        input.userId == null) {
        return false;
    }
    var colleagueNodeId = input.sourceNodeId === input.userId
        ? input.targetNodeId
        : input.sourceNodeId;
    var focusOrgId = demoUserPrimaryOrgUnitId(input.userId);
    var colleagueOrgId = demoUserPrimaryOrgUnitId(colleagueNodeId);
    return (focusOrgId != null &&
        colleagueOrgId != null &&
        focusOrgId !== colleagueOrgId);
};
var isTeamPersonToPersonEmailLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isTeamPersonToPersonChatLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isTeamPersonToPersonMeetLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isEmployeeScopePersonToExternalEmailLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        isExternalLinkInput(input);
};
var isEmployeeScopePersonToExternalChatLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        isExternalLinkInput(input);
};
var isEmployeeScopePersonToExternalMeetLinkInput = function (input) {
    return input.userId != null &&
        input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        isExternalLinkInput(input);
};
var isTeamPersonToExternalEmailLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        isExternalLinkInput(input);
};
var isTeamPersonToExternalChatLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        isExternalLinkInput(input);
};
var isTeamPersonToExternalMeetLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        isExternalLinkInput(input);
};
var isTeamPersonToCrossTeamEmailLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        (input.sourceNodeId === CROSS_TEAM_NODE_ID ||
            input.targetNodeId === CROSS_TEAM_NODE_ID);
};
var isTeamPersonToCrossTeamChatLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        (input.sourceNodeId === CROSS_TEAM_NODE_ID ||
            input.targetNodeId === CROSS_TEAM_NODE_ID);
};
var isTeamPersonToCrossTeamMeetLinkInput = function (input) {
    return input.orgUnitId !== null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        (input.sourceNodeId === CROSS_TEAM_NODE_ID ||
            input.targetNodeId === CROSS_TEAM_NODE_ID);
};
var isOrgTeamToOrgTeamEmailLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isOrgTeamToOrgTeamChatLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var isOrgTeamToOrgTeamMeetLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        !isExternalLinkInput(input) &&
        input.sourceNodeId > 0 &&
        input.targetNodeId > 0;
};
var buildOrgTeamChatLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '52', diff: getPercentageDiff(48, 52) },
                target: { value: '61', diff: getPercentageDiff(58, 61) },
                format: {
                    source: {
                        oneToOne: { value: '18', diff: getPercentageDiff(16, 18) },
                        group: { value: '34', diff: getPercentageDiff(32, 34) }
                    },
                    target: {
                        oneToOne: { value: '22', diff: getPercentageDiff(20, 22) },
                        group: { value: '39', diff: getPercentageDiff(38, 39) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'oneToOneVsGroup',
                stat: { value: '35.4% / 64.6%' }
            }
        ],
        topSenders: [
            {
                id: '34',
                name: 'Priya Sharma',
                subtitle: 'Programming',
                stat: { value: '41', diff: getPercentageDiff(38, 41) }
            },
            {
                id: '6',
                name: 'David Smith',
                subtitle: 'Marketing',
                stat: { value: '36', diff: getPercentageDiff(33, 36) }
            }
        ],
        topGroups: [
            {
                id: '901',
                name: 'Project Atlas',
                stat: { value: '28', diff: getPercentageDiff(24, 28) }
            },
            {
                id: '902',
                name: 'Release Coordination',
                stat: { value: '19', diff: getPercentageDiff(17, 19) }
            }
        ]
    }
}); };
var isOrgTeamToExternalEmailLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Email &&
        isExternalLinkInput(input);
};
var isOrgTeamToExternalChatLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        isExternalLinkInput(input);
};
var isOrgTeamToExternalMeetLinkInput = function (input) {
    return input.orgUnitId === null &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        isExternalLinkInput(input);
};
var buildOrgTeamExternalMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '14', diff: getPercentageDiff(12, 14) },
                target: { value: '14', diff: getPercentageDiff(12, 14) },
                format: {
                    source: {
                        oneToOne: { value: '5', diff: getPercentageDiff(4, 5) },
                        group: { value: '9', diff: getPercentageDiff(8, 9) }
                    },
                    target: {
                        oneToOne: { value: '5', diff: getPercentageDiff(4, 5) },
                        group: { value: '9', diff: getPercentageDiff(8, 9) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '6h 30m', diff: getPercentageDiff(20000, 23400) },
                target: { value: '6h 30m', diff: getPercentageDiff(20000, 23400) },
                format: {
                    source: {
                        oneToOne: { value: '2h', diff: getPercentageDiff(3600, 7200) },
                        group: {
                            value: '4h 30m',
                            diff: getPercentageDiff(16200, 16200)
                        }
                    },
                    target: {
                        oneToOne: { value: '2h', diff: getPercentageDiff(3600, 7200) },
                        group: {
                            value: '4h 30m',
                            diff: getPercentageDiff(16200, 16200)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '28m', diff: getPercentageDiff(1500, 1680) },
                target: { value: '28m', diff: getPercentageDiff(1500, 1680) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'employeeAttendanceDuration',
                stat: {
                    value: '12h 15m',
                    diff: getPercentageDiff(39600, 44100, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            { key: 'oneToOneVsGroup', stat: { value: '35.7% / 64.3%' } },
            {
                key: 'avgMeetingsPerMember',
                stat: { value: '3.5', diff: getPercentageDiff(3.1, 3.5) }
            },
            {
                key: 'externalShare',
                stat: { value: '46.7%', diff: getPercentagePointDiff(40, 46.7) }
            },
            {
                key: 'externalGuests',
                stat: { value: '8', diff: getPercentageDiff(7, 8) }
            },
            {
                key: 'avgMeetingDuration',
                stat: { value: '28m', diff: getPercentageDiff(1500, 1680) }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '21.4%', diff: getPercentagePointDiff(15, 21.4) }
            },
            {
                key: 'longMeetings',
                stat: { value: '14.3%', diff: getPercentagePointDiff(10, 14.3) }
            }
        ],
        topSenders: [
            {
                id: '34',
                name: 'Priya Sharma',
                stat: {
                    value: '6 / 3h 10m',
                    diff: getPercentageDiff(9000, 11400, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            }
        ],
        topPartners: [
            {
                id: 'vendor',
                name: 'vendor@acme.io',
                meetingCount: 4,
                durationSeconds: 7200,
                kindKey: 'external',
                diff: getPercentageDiff(5400, 7200, {
                    comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                })
            }
        ]
    }
}); };
var buildOrgTeamExternalChatLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '38', diff: getPercentageDiff(30, 38) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '22', diff: getPercentageDiff(18, 22) },
                        group: { value: '16', diff: getPercentageDiff(12, 16) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '47', diff: getPercentageDiff(40, 47) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '31', diff: getPercentageDiff(28, 31) },
                        group: { value: '16', diff: getPercentageDiff(12, 16) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '45% / 55%' }
            },
            {
                key: 'oneToOneVsGroup',
                stat: { value: '62.4% / 37.6%' }
            },
            {
                key: 'externalShare',
                stat: {
                    value: '41.2%',
                    diff: getPercentagePointDiff(38, 41.2)
                }
            },
            {
                key: 'avgPerSender',
                stat: { value: '6.3', diff: getPercentageDiff(5.1, 6.3) }
            },
            {
                key: 'externalPartners',
                stat: { value: '6', diff: getPercentageDiff(5, 6) }
            }
        ],
        topSenders: [
            {
                id: '18',
                name: 'Jane Doe',
                stat: { value: '14', diff: getPercentageDiff(12, 14) }
            },
            {
                id: '37',
                name: 'Sarah Chen',
                stat: { value: '11', diff: getPercentageDiff(10, 11) }
            }
        ],
        topPartners: [
            {
                id: 'ext-1',
                name: 'Sam Patel',
                emailCount: 22,
                sharePct: 0,
                kindKey: 'external',
                diff: getPercentageDiff(19, 22)
            },
            {
                id: 'ext-2',
                name: 'Jordan Lee',
                emailCount: 18,
                sharePct: 0,
                kindKey: 'external',
                diff: getPercentageDiff(16, 18)
            }
        ],
        topGroups: [
            {
                id: '801',
                name: 'Vendor Sync',
                stat: { value: '21', diff: getPercentageDiff(18, 21) }
            }
        ]
    }
}); };
var buildOrgTeamExternalEmailLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '43', diff: getPercentageDiff(33, 43) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            },
            {
                key: 'received',
                source: { value: '55', diff: getPercentageDiff(45, 55) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '44% / 56%' }
            },
            {
                key: 'externalShare',
                stat: {
                    value: '38.5%',
                    diff: getPercentagePointDiff(40, 38.5)
                }
            },
            {
                key: 'avgPerSender',
                stat: { value: '5.4', diff: getPercentageDiff(4.1, 5.4) }
            },
            {
                key: 'externalPartners',
                stat: { value: '5', diff: getPercentageDiff(8, 5) }
            }
        ],
        topSenders: [
            {
                id: '18',
                name: 'Jane Doe',
                stat: { value: '25', diff: getPercentageDiff(24, 25) }
            },
            {
                id: '37',
                name: 'Sarah Chen',
                stat: { value: '23', diff: getPercentageDiff(21, 23) }
            },
            {
                id: '6',
                name: 'David Smith',
                stat: { value: '17', diff: getPercentageDiff(16, 17) }
            }
        ],
        topPartners: [
            {
                id: 'ext-1',
                name: 'Sam Patel',
                emailCount: 25,
                sharePct: 0,
                kindKey: 'external',
                diff: getPercentageDiff(23, 25)
            },
            {
                id: 'ext-2',
                name: 'Jordan Lee',
                emailCount: 20,
                sharePct: 0,
                kindKey: 'external',
                diff: getPercentageDiff(17, 20)
            }
        ]
    }
}); };
var buildOrgTeamMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '18', diff: getPercentageDiff(16, 18) },
                target: { value: '18', diff: getPercentageDiff(16, 18) },
                format: {
                    source: {
                        oneToOne: { value: '6', diff: getPercentageDiff(5, 6) },
                        group: { value: '12', diff: getPercentageDiff(11, 12) }
                    },
                    target: {
                        oneToOne: { value: '6', diff: getPercentageDiff(5, 6) },
                        group: { value: '12', diff: getPercentageDiff(11, 12) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '9h 12m', diff: getPercentageDiff(28800, 33120) },
                target: { value: '9h 12m', diff: getPercentageDiff(28800, 33120) },
                format: {
                    source: {
                        oneToOne: { value: '2h', diff: getPercentageDiff(3600, 7200) },
                        group: {
                            value: '7h 12m',
                            diff: getPercentageDiff(25200, 25920)
                        }
                    },
                    target: {
                        oneToOne: { value: '2h', diff: getPercentageDiff(3600, 7200) },
                        group: {
                            value: '7h 12m',
                            diff: getPercentageDiff(25200, 25920)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '31m', diff: getPercentageDiff(1500, 1860) },
                target: { value: '31m', diff: getPercentageDiff(1500, 1860) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'employeeAttendanceDuration',
                stat: {
                    value: '22h 40m',
                    diff: getPercentageDiff(72000, 81600, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            {
                key: 'oneToOneVsGroup',
                stat: { value: '33.3% / 66.7%' }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '22.2%', diff: getPercentagePointDiff(15, 22.2) }
            },
            {
                key: 'longMeetings',
                stat: { value: '11.1%', diff: getPercentagePointDiff(8, 11.1) }
            }
        ],
        topSenders: [
            {
                id: '34',
                name: 'Priya Sharma',
                subtitle: 'Programming',
                stat: {
                    value: '7 / 3h 45m',
                    diff: getPercentageDiff(10800, 13500, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            {
                id: '6',
                name: 'David Smith',
                subtitle: 'Marketing',
                stat: {
                    value: '5 / 2h 30m',
                    diff: getPercentageDiff(7200, 9000, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            }
        ]
    }
}); };
var buildOrgTeamEmailLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '37', diff: getPercentageDiff(39, 37) },
                target: { value: '44', diff: getPercentageDiff(46, 44) }
            }
        ]
    },
    extended: {
        topSenders: [
            {
                id: '34',
                name: 'Priya Sharma',
                subtitle: 'Programming',
                stat: { value: '27', diff: getPercentageDiff(25, 27) }
            },
            {
                id: '6',
                name: 'David Smith',
                subtitle: 'Marketing',
                stat: { value: '25', diff: getPercentageDiff(24, 25) }
            },
            {
                id: '10',
                name: 'Emma Wilson',
                subtitle: 'Marketing',
                stat: { value: '25', diff: getPercentageDiff(23, 25) }
            }
        ]
    }
}); };
var buildTeamPersonToPersonChatLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '2', diff: getPercentageDiff(3, 2) },
                target: { value: '2', diff: getPercentageDiff(3, 2) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '1', diff: getPercentageDiff(0, 1) }
                    },
                    target: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '1', diff: getPercentageDiff(0, 1) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'avgResponseTimeSource',
                stat: {
                    value: '1h 45m',
                    diff: getPercentageDiff(6600, 6300, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            {
                key: 'avgResponseTimeTarget',
                stat: {
                    value: '50m',
                    diff: getPercentageDiff(3360, 3000, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            {
                key: 'sharedChatRooms',
                stat: { value: '5', diff: getPercentageDiff(1, 5) }
            }
        ],
        topGroups: [
            {
                id: '1',
                name: 'Launch War Room',
                stat: { value: '27', diff: getPercentageDiff(25, 27) }
            },
            {
                id: '2',
                name: 'Product Triage',
                stat: { value: '20', diff: getPercentageDiff(17, 20) }
            },
            {
                id: '3',
                name: 'Weekly Sync',
                stat: { value: '13', diff: getPercentageDiff(11, 13) }
            }
        ]
    }
}); };
var buildEmployeeScopeCrossTeamMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '1', diff: getPercentageDiff(0, 1) },
                target: { value: '1', diff: getPercentageDiff(0, 1) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(0, 1) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    },
                    target: {
                        oneToOne: { value: '1', diff: getPercentageDiff(0, 1) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: {
                    value: '1h',
                    diff: getPercentageDiff(0, 3600)
                },
                target: {
                    value: '1h',
                    diff: getPercentageDiff(0, 3600)
                },
                format: {
                    source: {
                        oneToOne: {
                            value: '34m',
                            diff: getPercentageDiff(600, 2040)
                        },
                        group: {
                            value: '26m',
                            diff: getPercentageDiff(960, 1560)
                        }
                    },
                    target: {
                        oneToOne: {
                            value: '34m',
                            diff: getPercentageDiff(600, 2040)
                        },
                        group: {
                            value: '26m',
                            diff: getPercentageDiff(960, 1560)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '1h', diff: getPercentageDiff(0, 3600) },
                target: { value: '1h', diff: getPercentageDiff(0, 3600) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'oneToOneVsGroup',
                stat: { value: '57% / 43%' }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '20%', diff: getPercentagePointDiff(16, 20) }
            },
            {
                key: 'longMeetings',
                stat: { value: '18%', diff: getPercentagePointDiff(16, 18) }
            }
        ]
    }
}); };
var buildTeamPersonToPersonMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '32', diff: getPercentageDiff(46, 32) },
                target: { value: '32', diff: getPercentageDiff(46, 32) },
                format: {
                    source: {
                        oneToOne: { value: '18', diff: getPercentageDiff(16, 18) },
                        group: { value: '14', diff: getPercentageDiff(13, 14) }
                    },
                    target: {
                        oneToOne: { value: '18', diff: getPercentageDiff(16, 18) },
                        group: { value: '14', diff: getPercentageDiff(13, 14) }
                    }
                }
            },
            {
                key: 'received',
                source: {
                    value: '15h 6m',
                    diff: getPercentageDiff(79200, 54360)
                },
                target: {
                    value: '15h 6m',
                    diff: getPercentageDiff(79200, 54360)
                },
                format: {
                    source: {
                        oneToOne: {
                            value: '8h 27m',
                            diff: getPercentageDiff(30600, 30420)
                        },
                        group: {
                            value: '6h 39m',
                            diff: getPercentageDiff(23940, 23940)
                        }
                    },
                    target: {
                        oneToOne: {
                            value: '8h 27m',
                            diff: getPercentageDiff(30600, 30420)
                        },
                        group: {
                            value: '6h 39m',
                            diff: getPercentageDiff(23940, 23940)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '28m', diff: getPercentageDiff(1700, 1680) },
                target: { value: '28m', diff: getPercentageDiff(1700, 1680) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'oneToOneVsGroup',
                stat: { value: '56.0% / 44.0%' }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '19.0%', diff: getPercentagePointDiff(11, 19) }
            },
            {
                key: 'longMeetings',
                stat: { value: '25.0%', diff: getPercentagePointDiff(17, 25) }
            }
        ]
    }
}); };
var buildTeamPersonToPersonEmailLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '17', diff: getPercentageDiff(11, 17) },
                target: { value: '10', diff: getPercentageDiff(6, 10) },
                format: {
                    source: {
                        oneToOne: { value: '17', diff: getPercentageDiff(11, 17) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    },
                    target: {
                        oneToOne: { value: '10', diff: getPercentageDiff(6, 10) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '10', diff: getPercentageDiff(6, 10) },
                target: { value: '17', diff: getPercentageDiff(11, 17) },
                format: {
                    source: {
                        oneToOne: { value: '10', diff: getPercentageDiff(6, 10) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    },
                    target: {
                        oneToOne: { value: '17', diff: getPercentageDiff(11, 17) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'avgReplyTimeSource',
                stat: {
                    value: '3h 20m',
                    diff: getPercentageDiff(12000, 12000, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            },
            {
                key: 'avgReplyTimeTarget',
                stat: {
                    value: '1h 54m',
                    diff: getPercentageDiff(6840, 6840, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            }
        ]
    }
}); };
var buildTeamPersonToExternalChatLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '2', diff: getPercentageDiff(1, 2) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '1', diff: getPercentageDiff(0, 1) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '1', diff: getPercentageDiff(1, 1) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '67% / 33%' }
            },
            {
                key: 'oneToOneVsGroup',
                stat: { value: '64% / 36%' }
            },
            {
                key: 'externalShare',
                stat: {
                    value: '13.0%',
                    diff: getPercentagePointDiff(11.1, 13)
                }
            },
            {
                key: 'externalPartners',
                stat: { value: '2', diff: getPercentageDiff(1, 2) }
            },
            {
                key: 'avgResponseTime',
                stat: {
                    value: '2h 40m',
                    diff: getPercentageDiff(9800, 9600, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            }
        ],
        topPartners: [
            {
                id: 'taylor',
                name: 'Taylor Brooks',
                emailCount: 21,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            },
            {
                id: 'alex',
                name: 'Alex Rivera',
                emailCount: 20,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            },
            {
                id: 'sam',
                name: 'Sam Patel',
                emailCount: 16,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            },
            {
                id: 'jordan',
                name: 'Jordan Lee',
                emailCount: 11,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            }
        ],
        topGroups: [
            {
                id: 'weekly-sync',
                name: 'Weekly Sync',
                stat: { value: '31', diff: getPercentageDiff(29, 31) }
            },
            {
                id: 'cs-hub',
                name: 'Customer Success Hub',
                stat: { value: '28', diff: getPercentageDiff(25, 28) }
            },
            {
                id: 'launch',
                name: 'Launch War Room',
                stat: { value: '21', diff: getPercentageDiff(19, 21) }
            },
            {
                id: 'triage',
                name: 'Product Triage',
                stat: { value: '14', diff: getPercentageDiff(11, 14) }
            }
        ]
    }
}); };
var buildTeamPersonToExternalEmailLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '10', diff: getPercentageDiff(10, 10) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            },
            {
                key: 'received',
                source: { value: '0', diff: getPercentageDiff(0, 0) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '100% / 0%' }
            },
            {
                key: 'externalShare',
                stat: ppDiff()
            },
            {
                key: 'externalPartners',
                stat: randStat(4, 8)
            },
            {
                key: 'avgResponseTime',
                stat: {
                    value: '57m',
                    diff: getPercentageDiff(3060, 3420, {
                        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                    })
                }
            }
        ],
        topPartners: [
            {
                id: 'sam',
                name: 'Sam Patel',
                emailCount: 20,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            },
            {
                id: 'jordan',
                name: 'Jordan Lee',
                emailCount: 15,
                sharePct: 0,
                diff: randDiff(),
                kindKey: 'external'
            }
        ]
    }
}); };
var ppDiff = function () {
    var previous = randInt(8, 22);
    var current = previous + randInt(-3, 5);
    return {
        value: "".concat(current.toFixed(1), "%"),
        diff: getPercentagePointDiff(previous, current)
    };
};
var buildTeamPersonToCrossTeamEmailLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '14', diff: getPercentageDiff(10, 14) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            },
            {
                key: 'received',
                source: { value: '9', diff: getPercentageDiff(7, 9) },
                target: { value: '0', diff: getPercentageDiff(0, 0) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '60.9% / 39.1%' }
            },
            {
                key: 'crossTeamShare',
                stat: ppDiff()
            },
            {
                key: 'crossTeamColleagues',
                stat: randStat(3, 9)
            }
        ],
        topPartnerSections: [
            {
                key: 'teams',
                rows: [
                    {
                        id: 'sales',
                        name: 'Sales',
                        emailCount: 12,
                        sharePct: 52,
                        diff: randDiff()
                    },
                    {
                        id: 'ops',
                        name: 'Operations',
                        emailCount: 11,
                        sharePct: 48,
                        diff: randDiff()
                    }
                ]
            },
            {
                key: 'ranked',
                rows: [
                    {
                        id: 'mia',
                        name: 'Mia Ortiz',
                        subtitle: 'Sales',
                        emailCount: 12,
                        scopedUserSentCount: 7,
                        partnerSentCount: 5,
                        sharePct: 0,
                        diff: randDiff()
                    }
                ]
            }
        ]
    }
}); };
var buildTeamPersonToCrossTeamChatLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '9', diff: getPercentageDiff(6, 9) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '5', diff: getPercentageDiff(3, 5) },
                        group: { value: '4', diff: getPercentageDiff(2, 4) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: { value: '9', diff: getPercentageDiff(6, 9) },
                target: { value: '0', diff: getPercentageDiff(0, 0) },
                format: {
                    source: {
                        oneToOne: { value: '5', diff: getPercentageDiff(4, 5) },
                        group: { value: '4', diff: getPercentageDiff(1, 4) }
                    },
                    target: {
                        oneToOne: { value: '0', diff: getPercentageDiff(0, 0) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'sentVsReceived',
                stat: { value: '50% / 50%' }
            },
            {
                key: 'oneToOneVsGroup',
                stat: { value: '55.6% / 44.4%' }
            },
            {
                key: 'crossTeamShare',
                stat: {
                    value: '42.9%',
                    diff: getPercentagePointDiff(37.5, 42.9)
                }
            },
            {
                key: 'crossTeamColleagues',
                stat: { value: '4', diff: getPercentageDiff(3, 4) }
            }
        ],
        topPartnerSections: [
            {
                key: 'teams',
                rows: [
                    {
                        id: 'workalizer',
                        name: 'Workalizer',
                        emailCount: 12,
                        sharePct: 33.3,
                        diff: randDiff()
                    },
                    {
                        id: 'programming',
                        name: 'Programming',
                        emailCount: 10,
                        sharePct: 27.8,
                        diff: randDiff()
                    },
                    {
                        id: 'sales',
                        name: 'Sales',
                        emailCount: 8,
                        sharePct: 22.2,
                        diff: randDiff()
                    },
                    {
                        id: 'unassigned',
                        name: 'Unassigned Employees',
                        emailCount: 6,
                        sharePct: 16.7,
                        diff: randDiff()
                    }
                ]
            },
            {
                key: 'ranked',
                rows: [
                    {
                        id: 'ben',
                        name: 'Ben Davis',
                        subtitle: 'Sales',
                        emailCount: 20,
                        sharePct: 0,
                        diff: randDiff()
                    },
                    {
                        id: 'mason',
                        name: 'Mason Walker',
                        subtitle: 'Sales',
                        emailCount: 14,
                        sharePct: 0,
                        diff: randDiff()
                    },
                    {
                        id: 'ethan',
                        name: 'Ethan Lewis',
                        subtitle: 'Sales',
                        emailCount: 12,
                        sharePct: 0,
                        diff: randDiff()
                    }
                ]
            }
        ],
        topGroups: [
            {
                id: 'triage',
                name: 'Product Triage',
                stat: { value: '29', diff: randDiff() }
            },
            {
                id: 'sync',
                name: 'Weekly Sync',
                stat: { value: '22', diff: randDiff() }
            },
            {
                id: 'cs',
                name: 'Customer Success Hub',
                stat: { value: '19', diff: randDiff() }
            }
        ]
    }
}); };
var buildTeamPersonToCrossTeamMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '1', diff: getPercentageDiff(1, 1) },
                target: { value: '1', diff: getPercentageDiff(1, 1) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    },
                    target: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '0', diff: getPercentageDiff(0, 0) }
                    }
                }
            },
            {
                key: 'received',
                source: {
                    value: '1h',
                    diff: getPercentageDiff(2232, 3600)
                },
                target: {
                    value: '1h',
                    diff: getPercentageDiff(2232, 3600)
                },
                format: {
                    source: {
                        oneToOne: {
                            value: '26m',
                            diff: getPercentageDiff(660, 1560)
                        },
                        group: {
                            value: '34m',
                            diff: getPercentageDiff(1440, 2040)
                        }
                    },
                    target: {
                        oneToOne: {
                            value: '26m',
                            diff: getPercentageDiff(660, 1560)
                        },
                        group: {
                            value: '34m',
                            diff: getPercentageDiff(1440, 2040)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '1h', diff: getPercentageDiff(2232, 3600) },
                target: { value: '1h', diff: getPercentageDiff(2232, 3600) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'oneToOneVsGroup',
                stat: { value: '43.0% / 57.0%' }
            },
            {
                key: 'crossTeamShare',
                stat: {
                    value: '0.4%',
                    diff: getPercentagePointDiff(0.5, 0.4)
                }
            },
            {
                key: 'crossTeamColleagues',
                stat: { value: '7', diff: getPercentageDiff(4, 7) }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '21.0%', diff: getPercentagePointDiff(18, 21) }
            },
            {
                key: 'longMeetings',
                stat: { value: '25.0%', diff: getPercentagePointDiff(22, 25) }
            }
        ],
        topPartnerSections: [
            {
                key: 'teams',
                rows: [
                    {
                        id: 'sales',
                        name: 'Sales',
                        meetingCount: 2,
                        durationSeconds: 3060,
                        diff: getPercentageDiff(1840, 3060)
                    },
                    {
                        id: 'programming',
                        name: 'Programming',
                        meetingCount: 2,
                        durationSeconds: 1800,
                        diff: getPercentageDiff(900, 1800)
                    },
                    {
                        id: 'unassigned',
                        name: 'Unassigned Employ...',
                        meetingCount: 2,
                        durationSeconds: 1800,
                        diff: getPercentageDiff(900, 1800)
                    },
                    {
                        id: 'workalizer',
                        name: 'Workalizer',
                        meetingCount: 3,
                        durationSeconds: 1800,
                        diff: getPercentageDiff(900, 1800)
                    }
                ]
            },
            {
                key: 'ranked',
                rows: [
                    {
                        id: 'logan',
                        name: '23. Logan Hall',
                        subtitle: 'Sales',
                        meetingCount: 14,
                        durationSeconds: 27720,
                        sharePct: 0,
                        diff: getPercentageDiff(25920, 27720)
                    },
                    {
                        id: 'noah',
                        name: '31. Noah Clark',
                        subtitle: 'Sales',
                        meetingCount: 14,
                        durationSeconds: 25200,
                        sharePct: 0,
                        diff: getPercentageDiff(23400, 25200)
                    },
                    {
                        id: 'john',
                        name: '19. John Johnson',
                        subtitle: 'Sales',
                        meetingCount: 7,
                        durationSeconds: 22260,
                        sharePct: 0,
                        diff: getPercentageDiff(20400, 22260)
                    }
                ]
            }
        ]
    }
}); };
var buildTeamPersonToExternalMeetLinkInspectorMock = function () { return ({
    summary: {
        metrics: [
            {
                key: 'sent',
                source: { value: '2', diff: getPercentageDiff(2, 2) },
                target: { value: '2', diff: getPercentageDiff(2, 2) },
                format: {
                    source: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '1', diff: getPercentageDiff(1, 1) }
                    },
                    target: {
                        oneToOne: { value: '1', diff: getPercentageDiff(1, 1) },
                        group: { value: '1', diff: getPercentageDiff(1, 1) }
                    }
                }
            },
            {
                key: 'received',
                source: {
                    value: '1h 26m',
                    diff: getPercentageDiff(5160, 5160)
                },
                target: {
                    value: '1h 26m',
                    diff: getPercentageDiff(5160, 5160)
                },
                format: {
                    source: {
                        oneToOne: {
                            value: '43m',
                            diff: getPercentageDiff(2280, 2580)
                        },
                        group: {
                            value: '43m',
                            diff: getPercentageDiff(2280, 2580)
                        }
                    },
                    target: {
                        oneToOne: {
                            value: '43m',
                            diff: getPercentageDiff(2280, 2580)
                        },
                        group: {
                            value: '43m',
                            diff: getPercentageDiff(2280, 2580)
                        }
                    }
                }
            },
            {
                key: 'avgMeetingLength',
                source: { value: '43m', diff: getPercentageDiff(2580, 2580) },
                target: { value: '43m', diff: getPercentageDiff(2580, 2580) }
            }
        ]
    },
    extended: {
        fields: [
            {
                key: 'oneToOneVsGroup',
                stat: { value: '50.0% / 50.0%' }
            },
            {
                key: 'externalShare',
                stat: {
                    value: '0.8%',
                    diff: getPercentagePointDiff(0.8, 0.8)
                }
            },
            {
                key: 'externalGuests',
                stat: { value: '6', diff: getPercentageDiff(3, 6) }
            },
            {
                key: 'largeMeetings5Plus',
                stat: { value: '20.0%', diff: getPercentagePointDiff(20, 20) }
            },
            {
                key: 'longMeetings',
                stat: { value: '26.0%', diff: getPercentagePointDiff(18, 26) }
            }
        ],
        topPartners: [
            {
                id: 'sam',
                name: 'Sam Patel',
                meetingCount: 10,
                durationSeconds: 27000,
                diff: getPercentageDiff(25740, 27000)
            },
            {
                id: 'alex',
                name: 'Alex Rivera',
                meetingCount: 10,
                durationSeconds: 21000,
                diff: getPercentageDiff(19800, 21000)
            },
            {
                id: 'taylor',
                name: 'Taylor Brooks',
                meetingCount: 6,
                durationSeconds: 18000,
                diff: getPercentageDiff(16800, 18000)
            },
            {
                id: 'jordan',
                name: 'Jordan Lee',
                meetingCount: 6,
                durationSeconds: 14400,
                diff: getPercentageDiff(13200, 14400)
            }
        ]
    }
}); };
export var buildDemoCommunicationNetworkGraphLinkInspectorResponse = function (input) {
    if (isEmployeeScopePersonToExternalEmailLinkInput(input)) {
        return buildTeamPersonToExternalEmailLinkInspectorMock();
    }
    if (isEmployeeScopePersonToExternalChatLinkInput(input)) {
        return buildTeamPersonToExternalChatLinkInspectorMock();
    }
    if (isEmployeeScopePersonToExternalMeetLinkInput(input)) {
        return buildTeamPersonToExternalMeetLinkInspectorMock();
    }
    if (isTeamPersonToExternalEmailLinkInput(input)) {
        return buildTeamPersonToExternalEmailLinkInspectorMock();
    }
    if (isTeamPersonToExternalChatLinkInput(input)) {
        return buildTeamPersonToExternalChatLinkInspectorMock();
    }
    if (isTeamPersonToExternalMeetLinkInput(input)) {
        return buildTeamPersonToExternalMeetLinkInspectorMock();
    }
    if (isTeamPersonToCrossTeamMeetLinkInput(input)) {
        return buildTeamPersonToCrossTeamMeetLinkInspectorMock();
    }
    if (isTeamPersonToCrossTeamChatLinkInput(input)) {
        return buildTeamPersonToCrossTeamChatLinkInspectorMock();
    }
    if (isTeamPersonToCrossTeamEmailLinkInput(input)) {
        return buildTeamPersonToCrossTeamEmailLinkInspectorMock();
    }
    if (isEmployeeScopePersonToPersonEmailLinkInput(input)) {
        return buildTeamPersonToPersonEmailLinkInspectorMock();
    }
    if (isEmployeeScopePersonToPersonChatLinkInput(input)) {
        return buildTeamPersonToPersonChatLinkInspectorMock();
    }
    if (isEmployeeScopeCrossTeamPersonToPersonMeetLinkInput(input)) {
        return buildEmployeeScopeCrossTeamMeetLinkInspectorMock();
    }
    if (isEmployeeScopePersonToPersonMeetLinkInput(input)) {
        return buildTeamPersonToPersonMeetLinkInspectorMock();
    }
    if (isTeamPersonToPersonEmailLinkInput(input)) {
        return buildTeamPersonToPersonEmailLinkInspectorMock();
    }
    if (isTeamPersonToPersonChatLinkInput(input)) {
        return buildTeamPersonToPersonChatLinkInspectorMock();
    }
    if (isTeamPersonToPersonMeetLinkInput(input)) {
        return buildTeamPersonToPersonMeetLinkInspectorMock();
    }
    if (isOrgTeamToOrgTeamEmailLinkInput(input)) {
        return buildOrgTeamEmailLinkInspectorMock();
    }
    if (isOrgTeamToOrgTeamChatLinkInput(input)) {
        return buildOrgTeamChatLinkInspectorMock();
    }
    if (isOrgTeamToOrgTeamMeetLinkInput(input)) {
        return buildOrgTeamMeetLinkInspectorMock();
    }
    if (isOrgTeamToExternalEmailLinkInput(input)) {
        return buildOrgTeamExternalEmailLinkInspectorMock();
    }
    if (isOrgTeamToExternalChatLinkInput(input)) {
        return buildOrgTeamExternalChatLinkInspectorMock();
    }
    if (isOrgTeamToExternalMeetLinkInput(input)) {
        return buildOrgTeamExternalMeetLinkInspectorMock();
    }
    var isExternalLink = isExternalLinkInput(input);
    var isPersonToPersonChat = input.channel === CommunicationNetworkGraphChannelEnum.Chat &&
        !isExternalLink &&
        !isTeamPersonToPersonChatLinkInput(input);
    var _a = buildMetricStats(input.channel, isExternalLink), sourceSent = _a.sourceSent, targetSent = _a.targetSent, sourceReceived = _a.sourceReceived, targetReceived = _a.targetReceived, sourceAvgMeetingLength = _a.sourceAvgMeetingLength, targetAvgMeetingLength = _a.targetAvgMeetingLength;
    var defaultMetrics = input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        !isExternalLink &&
        sourceAvgMeetingLength &&
        targetAvgMeetingLength
        ? [
            {
                key: 'sent',
                source: sourceSent,
                target: targetSent,
                format: randFormatBreakdown(input.channel, 'sent')
            },
            {
                key: 'received',
                source: sourceReceived,
                target: targetReceived,
                format: randFormatBreakdown(input.channel, 'received')
            },
            {
                key: 'avgMeetingLength',
                source: sourceAvgMeetingLength,
                target: targetAvgMeetingLength,
                format: randFormatBreakdown(input.channel, 'received')
            }
        ]
        : [
            {
                key: 'sent',
                source: sourceSent,
                target: targetSent,
                format: randFormatBreakdown(input.channel, 'sent')
            },
            {
                key: 'received',
                source: sourceReceived,
                target: targetReceived,
                format: randFormatBreakdown(input.channel, 'received')
            }
        ];
    var response = {
        summary: {
            metrics: isPersonToPersonChat
                ? [
                    {
                        key: 'messages',
                        source: sourceSent,
                        target: targetSent,
                        format: randFormatBreakdown(input.channel, 'sent')
                    },
                    {
                        key: 'reactions',
                        source: { value: '1', diff: getPercentageDiff(0, 1) },
                        target: { value: '0', diff: getPercentageDiff(0, 0) },
                        format: randFormatBreakdown(input.channel, 'sent')
                    }
                ]
                : defaultMetrics
        }
    };
    if (input.channel === CommunicationNetworkGraphChannelEnum.Email) {
        if (isExternalLink) {
            response.extended = {
                topPartners: buildDemoExternalLinkTopPartners()
            };
            return response;
        }
        if (isTeamPersonToPersonEmailLinkInput(input)) {
            return buildTeamPersonToPersonEmailLinkInspectorMock();
        }
        if (isTeamPersonToPersonChatLinkInput(input)) {
            return buildTeamPersonToPersonChatLinkInspectorMock();
        }
        response.extended = {
            fields: [
                {
                    key: 'avgResponseTimeOneToOne',
                    stat: {
                        value: "".concat(randInt(20, 90), "min"),
                        diff: randDurationDiff()
                    }
                },
                {
                    key: 'sharedEmailThreads',
                    stat: {
                        value: String(randInt(1, 8)),
                        diff: randDiff()
                    }
                }
            ]
        };
        return response;
    }
    if (isPersonToPersonChat && !isExternalLink) {
        response.extended = {
            fields: [
                {
                    key: 'avgResponseTimeOneToOne',
                    stat: {
                        value: "".concat(randInt(0, 45), "min"),
                        diff: randDurationDiff()
                    }
                },
                {
                    key: 'sharedChatRooms',
                    stat: {
                        value: String(randInt(1, 5)),
                        diff: randDiff()
                    }
                }
            ]
        };
    }
    if (input.channel === CommunicationNetworkGraphChannelEnum.Meet &&
        !isExternalLink) {
        response.extended = {
            fields: [
                {
                    key: 'longMeetings',
                    stat: {
                        value: "".concat((randInt(20, 120) / 10).toFixed(1), "%"),
                        diff: randDiff()
                    }
                }
            ]
        };
    }
    if (isExternalLink &&
        input.channel === CommunicationNetworkGraphChannelEnum.Chat) {
        response.extended = {
            topPartners: buildDemoExternalLinkTopPartners()
        };
    }
    if (isExternalLink &&
        input.channel === CommunicationNetworkGraphChannelEnum.Meet) {
        response.extended = {
            fields: [
                {
                    key: 'oneToOneVsGroup',
                    stat: { value: '33% / 67%' }
                },
                {
                    key: 'avgMeetingsPerMember',
                    stat: { value: '3', diff: getPercentageDiff(3, 3) }
                }
            ],
            topPartners: [
                __assign(__assign({}, buildDemoExternalLinkTopPartners()[0]), { sharePct: 67, diff: getPercentageDiff(33, 67), meetingCount: 2, durationSeconds: 7380 }),
                __assign(__assign({}, buildDemoExternalLinkTopPartners()[1]), { sharePct: 33, diff: getPercentageDiff(67, 33), meetingCount: 1, durationSeconds: 4380 })
            ]
        };
    }
    return response;
};
